export type BezierSeg = {
  cx1: number;
  cy1: number;
  cx2: number;
  cy2: number;
  x: number;
  y: number;
};

export type EdgeProfile = {
  a: BezierSeg[];
  b: BezierSeg[];
};

function makeClassicProfile(
  width: number,
  depth: number,
  neckRatio = 0.33,
  asymmetry = 0,
  micro = 0.07
): EdgeProfile {
  const left = 0.5 - width / 2;
  const clampedNeckRatio = Math.max(0.3, Math.min(0.35, neckRatio));
  const clampedAsym = Math.max(-0.08, Math.min(0.08, asymmetry));
  const clampedMicro = Math.max(0.05, Math.min(0.1, micro));
  const neckW = width * clampedNeckRatio;
  const neckL = 0.5 - neckW / 2;
  const neckR = 0.5 + neckW / 2;

  // "Гриб": узкая шейка и почти круглая шляпка.
  const neckY = 0.5 + depth * 0.22;
  const shoulderY = 0.5 + depth * 0.66;
  const capY = 0.5 + depth * 0.94;
  const peakY = 0.5 + depth;
  const leftShoulderX = 0.5 - width * (0.215 + clampedAsym * 0.5);
  const rightShoulderX = 0.5 + width * (0.215 - clampedAsym * 0.5);

  // Лёгкая "картонная" органика без ломки силуэта.
  const leftMicroX = width * clampedMicro;
  const rightMicroX = width * (clampedMicro * 0.85);
  const leftMicroY = depth * (clampedMicro * 0.6);
  const rightMicroY = depth * (clampedMicro * 0.5);

  return {
    a: [
      {
        // Вход -> шейка
        cx1: left + width * (0.08 - clampedAsym * 0.12),
        cy1: 0.5 + leftMicroY * 0.08,
        cx2: neckL - width * (0.1 + clampedAsym * 0.15) - leftMicroX * 0.35,
        cy2: neckY * (0.95 + clampedAsym * 0.08),
        x: neckL,
        y: neckY,
      },
      {
        // Шейка -> левая сторона шляпки
        cx1: neckL + width * (0.02 - clampedAsym * 0.08),
        cy1: shoulderY + leftMicroY * 0.15,
        cx2: leftShoulderX - width * 0.04 - leftMicroX * 0.3,
        cy2: capY + leftMicroY * 0.1,
        x: leftShoulderX,
        y: capY,
      },
      {
        // Левая сторона шляпки -> вершина (крупная округлая головка)
        cx1: leftShoulderX + width * 0.05 + leftMicroX * 0.22,
        cy1: peakY - leftMicroY * 0.28,
        cx2: 0.5 - width * (0.115 + clampedAsym * 0.18),
        cy2: peakY + leftMicroY * 0.1,
        x: 0.5,
        y: peakY,
      },
    ],
    b: [
      {
        // Вершина -> правая сторона шляпки
        cx1: 0.5 + width * (0.115 - clampedAsym * 0.18),
        cy1: peakY + rightMicroY * 0.1,
        cx2: rightShoulderX - width * 0.05 - rightMicroX * 0.22,
        cy2: peakY - rightMicroY * 0.28,
        x: rightShoulderX,
        y: capY,
      },
      {
        // Правая сторона шляпки -> шейка
        cx1: rightShoulderX + width * 0.04 + rightMicroX * 0.3,
        cy1: capY + rightMicroY * 0.1,
        cx2: neckR - width * (0.02 + clampedAsym * 0.08),
        cy2: shoulderY + rightMicroY * 0.15,
        x: neckR,
        y: neckY,
      },
      {
        // Шейка -> выход
        cx1: neckR + width * (0.1 - clampedAsym * 0.15) + rightMicroX * 0.35,
        cy1: neckY * (0.95 - clampedAsym * 0.08),
        cx2: 1 - width * (0.08 - clampedAsym * 0.12),
        cy2: 0.5 + rightMicroY * 0.08,
        x: 1,
        y: 0.5,
      },
    ],
  };
}

export const EDGE_PROFILES: EdgeProfile[] = [
  makeClassicProfile(0.66, 0.5, 0.3, -0.05, 0.06),
  makeClassicProfile(0.64, 0.49, 0.31, 0.04, 0.07),
  makeClassicProfile(0.68, 0.5, 0.32, -0.03, 0.08),
  makeClassicProfile(0.62, 0.48, 0.33, 0.06, 0.06),
  makeClassicProfile(0.67, 0.5, 0.34, -0.04, 0.09),
  makeClassicProfile(0.65, 0.49, 0.3, 0.05, 0.07),
  makeClassicProfile(0.69, 0.5, 0.31, -0.02, 0.08),
  makeClassicProfile(0.63, 0.48, 0.32, 0.03, 0.06),
  makeClassicProfile(0.66, 0.49, 0.34, -0.06, 0.09),
  makeClassicProfile(0.64, 0.5, 0.3, 0.02, 0.07),
];

export function getEdgeProfile(id: number): EdgeProfile {
  const count = EDGE_PROFILES.length;
  if (count === 0) {
    return { a: [], b: [] };
  }
  const numericId = Number.isFinite(id) ? Math.trunc(id) : 0;
  const normalized = ((numericId % count) + count) % count;
  return EDGE_PROFILES[normalized];
}
